import { useEffect, useRef, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ChevronRight, ListChecks } from 'lucide-react';
import { IPD_TASKS_CHANGED_EVENT, wardTasksPath, type StaffDutyWard } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { useRealtimeInvalidate } from '@/lib/realtime';
import { delayUntil } from '@/components/staff-alert-view';
import { nextDueChange, totalDue, wardDueStrips, type DueCounts, type WardDueStrip } from './due-tasks';
import { fetchWardTasks, wardTasksKey } from './task-queries';

const REFRESH_AFTER_MS = 60_000;

function useWakeAt(at: number | null, onWake: () => void) {
  const handler = useRef(onWake);
  handler.current = onWake;
  useEffect(() => {
    const delay = at === null ? null : delayUntil(new Date(at).toISOString());
    if (delay === null) return undefined;
    let pending = false;
    const timer = setTimeout(() => {
      if (document.visibilityState === 'visible') handler.current();
      else pending = true;
    }, delay);
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !pending) return;
      pending = false;
      handler.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [at]);
}

export interface DueNotice {
  strips: WardDueStrip[];
  total: DueCounts;
  refreshIfOld: () => void;
}

export function useDueNotice(wards: readonly StaffDutyWard[], enabled: boolean): DueNotice {
  const ids = enabled ? wards.map((ward) => ward.id) : [];
  const results = useQueries({
    queries: ids.map((id) => ({ queryKey: wardTasksKey(id), queryFn: () => fetchWardTasks(id), staleTime: REFRESH_AFTER_MS })),
  });
  const connected = useRealtimeInvalidate(
    ids.map((id) => `ward:${id}`),
    [IPD_TASKS_CHANGED_EVENT],
    ids.map((id) => wardTasksKey(id)),
  );
  const [now, setNow] = useState(() => Date.now());
  const stamp = results.reduce((sum, result) => sum + result.dataUpdatedAt, 0);
  useEffect(() => setNow(Date.now()), [stamp]);

  const refreshIfOld = () => {
    const at = Date.now();
    setNow(at);
    for (const result of results) {
      if (result.dataUpdatedAt > 0 && at - result.dataUpdatedAt > REFRESH_AFTER_MS && !result.isFetching) void result.refetch();
    }
  };
  const refresh = useRef(refreshIfOld);
  refresh.current = refreshIfOld;
  const wasConnected = useRef(connected);
  useEffect(() => {
    if (connected && !wasConnected.current) refresh.current();
    wasConnected.current = connected;
  }, [connected]);

  const windowOf = (wardId: string) => results[ids.indexOf(wardId)]?.data;
  const tasks = results.flatMap((result) => result.data?.data ?? []);
  useWakeAt(enabled ? nextDueChange(tasks, now) : null, () => setNow(Date.now()));
  const strips = enabled ? wardDueStrips(wards, windowOf, now) : [];
  return { strips, total: totalDue(strips), refreshIfOld };
}

export function dueBellLabel(total: DueCounts): string | null {
  return total.due + total.overdue > 0 ? tr('ipd', 'io_due_bell_label', { due: total.due, overdue: total.overdue }) : null;
}

export function DueStrips({ strips, onOpen }: { strips: readonly WardDueStrip[]; onOpen: (path: string) => void }) {
  if (strips.length === 0) return null;
  return (
    <ul className="space-y-1.5 border-b p-2" aria-label={tr('ipd', 'io_due_title')}>
      {strips.map((strip) => (
        <li key={strip.ward.id}>
          <button
            type="button"
            onClick={() => onOpen(wardTasksPath(strip.ward.id))}
            className="focus-ring flex w-full items-center gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-start text-sm text-foreground transition-colors hover:bg-warning/15"
          >
            <ListChecks className="h-4 w-4 shrink-0 text-warning" aria-hidden />
            <span className="min-w-0 flex-1 leading-snug [overflow-wrap:anywhere]">
              {tr('ipd', 'io_due_strip', { due: strip.due, overdue: strip.overdue, ward: strip.ward.name })}
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground rtl:-scale-x-100" aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
}
